import { supabase } from './supabase.js';
import { API_BASE_URL } from '../utils/constants.js';

const KEYCARD_PREFIX = 'fooview:key:v1:';
const KEYCARD_CREDENTIAL_STORAGE_PREFIX = 'fooview:keycard-credential:v1';
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function base64UrlEncode(bytes) {
  const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const str = btoa(String.fromCharCode(...data));
  return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(str) {
  const padding = '==='.slice(0, (4 - (str.length % 4)) % 4);
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function deriveAesKey(passphrase, salt) {
  const material = await crypto.subtle.importKey('raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: 180000 },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function requirePassphrase(passphrase) {
  if (!passphrase || passphrase.length < 8) {
    throw new Error('Phrase secrète de 8 caractères minimum requise.');
  }
}

function optionalPassphrase(passphrase) {
  return String(passphrase || '').trim();
}

function credentialStorageKey(userId) {
  return `${KEYCARD_CREDENTIAL_STORAGE_PREFIX}:${userId}`;
}

function saveStoredCredential(userId, credential) {
  try {
    localStorage.setItem(credentialStorageKey(userId), JSON.stringify(credential));
  } catch {}
}

function readStoredCredential(userId) {
  try {
    const raw = localStorage.getItem(credentialStorageKey(userId));
    if (!raw) return null;
    const credential = JSON.parse(raw);
    if (credential?.email && credential?.password && credential?.user_id === userId) return credential;
  } catch {}
  return null;
}

async function registerKeycardCredential(session) {
  const stored = readStoredCredential(session.user.id);
  if (stored) return stored;

  const res = await fetch(`${API_BASE_URL}/api/keycard/credential`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json'
    },
    body: '{}'
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Keycard refusée (${res.status})`);
  if (!data.email || !data.password || data.user_id !== session.user.id) {
    throw new Error('Credential keycard invalide.');
  }
  const credential = {
    auth: 'password',
    user_id: data.user_id,
    email: data.email,
    password: data.password
  };
  saveStoredCredential(session.user.id, credential);
  return credential;
}

export async function signInWithAnonymousKeycard(pseudo = '') {
  if (!supabase) throw new Error('Supabase non configuré.');
  const cleanPseudo = pseudo.trim().replace(/^@/, '').slice(0, 28) || `joueur_${crypto.randomUUID().slice(0, 8)}`;
  const { data, error } = await supabase.auth.signInAnonymously({
    options: {
      data: { pseudo: cleanPseudo, full_name: cleanPseudo }
    }
  });
  if (error) throw error;
  return data.session;
}

export async function exportCurrentKeycard(passphrase) {
  if (!supabase) throw new Error('Supabase non configuré.');
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token || !session?.refresh_token) {
    throw new Error('Aucune session à sauvegarder.');
  }
  const cleanPassphrase = optionalPassphrase(passphrase);
  const credential = await registerKeycardCredential(session);
  const payload = {
    v: 2,
    app: 'Fooview',
    auth: 'password',
    user_id: credential.user_id,
    email: credential.email,
    password: credential.password,
    created_at: new Date().toISOString()
  };
  if (!cleanPassphrase) {
    const keycard = {
      v: 2,
      app: 'Fooview',
      alg: 'none',
      warning: 'unencrypted-session-keycard',
      data: base64UrlEncode(encoder.encode(JSON.stringify(payload)))
    };
    return `${KEYCARD_PREFIX}${base64UrlEncode(encoder.encode(JSON.stringify(keycard)))}`;
  }
  requirePassphrase(cleanPassphrase);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveAesKey(cleanPassphrase, salt);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoder.encode(JSON.stringify(payload)));
  const keycard = {
    v: 1,
    app: 'Fooview',
    alg: 'PBKDF2-SHA256+A256GCM',
    kdf: { iterations: 180000, salt: base64UrlEncode(salt) },
    iv: base64UrlEncode(iv),
    data: base64UrlEncode(ciphertext)
  };
  return `${KEYCARD_PREFIX}${base64UrlEncode(encoder.encode(JSON.stringify(keycard)))}`;
}

export async function importKeycard(serialized, passphrase) {
  if (!supabase) throw new Error('Supabase non configuré.');
  const trimmed = String(serialized || '').trim();
  if (!trimmed) throw new Error('Keycard vide.');
  const encoded = trimmed.startsWith(KEYCARD_PREFIX) ? trimmed.slice(KEYCARD_PREFIX.length) : trimmed;
  let keycard;
  try {
    keycard = JSON.parse(decoder.decode(base64UrlDecode(encoded)));
  } catch {
    throw new Error('Format de keycard invalide.');
  }
  if (keycard.app !== 'Fooview' || ![1, 2].includes(keycard.v)) throw new Error('Keycard Fooview invalide.');
  let payload;
  if (keycard.alg === 'none') {
    try {
      payload = JSON.parse(decoder.decode(base64UrlDecode(keycard.data)));
    } catch {
      throw new Error('Keycard non protégée endommagée.');
    }
  } else {
    const cleanPassphrase = optionalPassphrase(passphrase);
    if (!cleanPassphrase) throw new Error('Cette keycard est protégée par une phrase secrète.');
    requirePassphrase(cleanPassphrase);
    const key = await deriveAesKey(cleanPassphrase, base64UrlDecode(keycard.kdf.salt));
    let decrypted;
    try {
      decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: base64UrlDecode(keycard.iv) },
        key,
        base64UrlDecode(keycard.data)
      );
    } catch {
      throw new Error('Phrase secrète incorrecte ou keycard endommagée.');
    }
    payload = JSON.parse(decoder.decode(decrypted));
  }
  if (payload.auth === 'password') {
    if (!payload.email || !payload.password) throw new Error('Keycard incomplète.');
    const { data, error } = await supabase.auth.signInWithPassword({
      email: payload.email,
      password: payload.password
    });
    if (error) throw new Error(error.message || 'Connexion keycard refusée.');
    if (data?.session?.user?.id) {
      saveStoredCredential(data.session.user.id, {
        auth: 'password',
        user_id: data.session.user.id,
        email: payload.email,
        password: payload.password
      });
    }
    return data.session;
  }

  if (!payload.access_token || !payload.refresh_token) throw new Error('Keycard incomplète.');

  let refreshError;
  try {
    const refreshed = await supabase.auth.refreshSession({ refresh_token: payload.refresh_token });
    if (!refreshed.error && refreshed.data?.session) return refreshed.data.session;
    refreshError = refreshed.error;
  } catch (error) {
    refreshError = error;
  }

  const { data, error } = await supabase.auth.setSession({
    access_token: payload.access_token,
    refresh_token: payload.refresh_token
  });
  if (error) throw new Error(error.message || refreshError?.message || 'Session keycard refusée.');
  return data.session;
}

export async function downloadCurrentKeycard(passphrase) {
  const keycard = await exportCurrentKeycard(passphrase);
  const { data: { user } } = await supabase.auth.getUser();
  const blob = new Blob([keycard], { type: 'application/vnd.fooview.key+text' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fooview-${(user?.id || 'key').slice(0, 8)}.fooviewkey`;
  a.click();
  URL.revokeObjectURL(url);
  return keycard;
}

export async function readTextFile(file) {
  return file.text();
}
